import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { fetchRoadCodeOverview, fetchRoadCodeSeries, ROAD_CODE_KEY, startRoadCodeSeries } from '../../api/roadCode';
import { apiMessage, type RoadCodeSeries, type SeriesStart } from '../../lib/roadCode';
import { CodeHome } from './CodeHome';
import { LessonReader, LessonsList } from './Lessons';
import { SeriesPlayer } from './SeriesPlayer';
import { SeriesResultView } from './SeriesResultView';

// Onglet « Code » de l'espace élève : révision du code de la route, ouverte par l'auto-école après
// l'achat d'accès dans la Boutique Permio. Un écran à la fois (accueil, série, résultat, fiches) ; le
// bouton retour du téléphone ramène à l'accueil de l'onglet au lieu de quitter l'application.

type Screen = { kind: 'home' } | { kind: 'play' | 'result'; id: string } | { kind: 'lessons' };

const seriesKey = (id: string) => [...ROAD_CODE_KEY, 'series', id] as const;

/** Une entrée d'historique tant qu'on est hors de l'accueil : « retour » y ramène. */
function useBackToHome(away: boolean, goHome: () => void) {
  const pushed = useRef(false);
  const home = useRef(goHome);
  home.current = goHome;
  useEffect(() => {
    const onPop = () => {
      if (!pushed.current) return;
      pushed.current = false;
      home.current();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    if (away && !pushed.current) {
      window.history.pushState({ permioCode: true }, '');
      pushed.current = true;
    } else if (!away && pushed.current) {
      pushed.current = false;
      window.history.back();
    }
  }, [away]);
}

export default function CodeView() {
  const client = useQueryClient();
  const overviewQuery = useQuery({ queryKey: ROAD_CODE_KEY, queryFn: fetchRoadCodeOverview, staleTime: 30_000 });
  const overview = overviewQuery.data;
  const [screen, setScreen] = useState<Screen>({ kind: 'home' });
  /** Fiche ouverte par-dessus (depuis la liste, ou depuis la correction d'une question). */
  const [lesson, setLesson] = useState<string | null>(null);
  const goHome = () => {
    setLesson(null);
    setScreen({ kind: 'home' });
    void client.invalidateQueries({ queryKey: ROAD_CODE_KEY, exact: true });
  };
  useBackToHome(screen.kind !== 'home' || lesson !== null, goHome);

  const seriesId = screen.kind === 'play' || screen.kind === 'result' ? screen.id : null;
  const seriesQuery = useQuery({
    queryKey: seriesKey(seriesId ?? ''),
    queryFn: () => fetchRoadCodeSeries(seriesId!),
    enabled: !!seriesId,
    staleTime: Infinity,
  });
  const series = seriesQuery.data;

  const start = useMutation({
    mutationFn: (value: SeriesStart) => startRoadCodeSeries(value),
    onSuccess: (created) => {
      client.setQueryData(seriesKey(created.id), created);
      setLesson(null);
      setScreen({ kind: 'play', id: created.id });
    },
  });
  /** Série à reprendre (en cours) ou à relire (terminée) : relue à l'ouverture. */
  const openSeries = (id: string, kind: 'play' | 'result') => {
    start.reset();
    void client.invalidateQueries({ queryKey: seriesKey(id) });
    setScreen({ kind, id });
  };
  /** Série finie : relue avec toutes ses corrections, puis le résultat. */
  const finish = async () => {
    await seriesQuery.refetch();
    if (seriesId) setScreen({ kind: 'result', id: seriesId });
  };
  const update = (next: RoadCodeSeries) => client.setQueryData(seriesKey(next.id), next);

  if (overviewQuery.isPending)
    return (
      <p className="flex items-center gap-2 text-sm text-secondary">
        <Loader2 size={16} className="animate-spin" aria-hidden /> Chargement de la révision du code…
      </p>
    );
  if (!overview || overviewQuery.isError)
    return <p className="text-sm text-danger-dark">La révision du code est indisponible pour le moment. Réessayez plus tard.</p>;
  if (overview.access.status === 'none')
    return <p className="text-sm text-secondary">Votre auto-école ne vous a pas encore ouvert la révision du code. Demandez-lui l’accès.</p>;

  const themes = overview.themes;
  return (
    <>
      {screen.kind === 'lessons' ? (
        <LessonsList onBack={goHome} onOpen={setLesson} />
      ) : (
        <CodeHome
          overview={overview}
          starting={start.isPending}
          startError={start.isError ? apiMessage(start.error, 'Impossible de commencer la série. Réessayez.') : null}
          onStart={(value) => start.mutate(value)}
          onResume={(id) => openSeries(id, 'play')}
          onOpenLessons={() => setScreen({ kind: 'lessons' })}
          onOpenResult={(id) => openSeries(id, 'result')}
        />
      )}

      {seriesId && seriesQuery.isError && (
        <p role="alert" className="fixed inset-x-4 bottom-24 z-[65] rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark shadow-pop">
          {apiMessage(seriesQuery.error, 'Série indisponible pour le moment.')}{' '}
          <button type="button" className="font-semibold underline" onClick={goHome}>
            Retour
          </button>
        </p>
      )}
      {screen.kind === 'result' && series?.status === 'finished' && (
        <SeriesResultView
          series={series}
          themes={themes}
          onClose={goHome}
          starting={start.isPending}
          onStart={(value) => start.mutate(value)}
        />
      )}
      {screen.kind === 'play' && series && (
        <SeriesPlayer
          key={series.id}
          series={series}
          themes={themes}
          onUpdate={update}
          onRefresh={() => void seriesQuery.refetch()}
          onClose={goHome}
          onFinished={() => void finish()}
          onOpenLesson={setLesson}
        />
      )}
      {lesson && <LessonReader key={lesson} lessonId={lesson} onOpen={setLesson} onClose={() => setLesson(null)} />}
    </>
  );
}
